import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { prepareImage } from '../../components/docTools.js';
import { Modal } from './ws.jsx';
import { usePrivateImage } from './DocLibrary.jsx';
import EmailSignatureEditor from '../../components/SignatureEditor.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// Signatures (migration 036): the company's signatories, each user's own
// ("Ma signature"), the signature blocks of a document and a template's
// defaults. Pictures are private files (shown through the API).

const FONTS = [['Dancing Script', 'Dancing+Script:wght@600'], ['Great Vibes', 'Great+Vibes'], ['Caveat', 'Caveat:wght@600'], ['Satisfy', 'Satisfy']];
function loadFonts() {
  if (document.getElementById('gf-signature-fonts')) return;
  const l = document.createElement('link');
  l.id = 'gf-signature-fonts'; l.rel = 'stylesheet';
  l.href = `https://fonts.googleapis.com/css2?${FONTS.map(([, f]) => `family=${f}`).join('&')}&display=swap`;
  document.head.appendChild(l);
}
export function SignaturePicture({ id, version, className = 'sig-img', alt = '' }) {
  const url = usePrivateImage(id ? `/workspace/doc-signatories/${id}/picture?v=${encodeURIComponent(version || '')}` : null);
  return url ? <img src={url} alt={alt} className={className} /> : <span className={`${className} sig-img-empty`} aria-hidden="true" />;
}

// ── Drawing pad (mouse, finger, pen) ────────────────────────────────────
function DrawPad({ onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const drawing = useRef(false);
  const used = useRef(false);
  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = c.offsetHeight * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio); ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0b1f38';
  }, []);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = ref.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext('2d'); ctx.lineTo(...pos(e)); ctx.stroke(); used.current = true; };
  const up = () => { if (!drawing.current) return; drawing.current = false; if (used.current) ref.current.toBlob((b) => onChange(b), 'image/png'); };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); used.current = false; onChange(null); };
  return (
    <div className="sig-pad-wrap">
      <canvas ref={ref} className="sig-pad" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} aria-label={t('sig.drawHere')} role="img" />
      <button type="button" className="btn btn-link btn-sm" onClick={clear}>{t('sig.clear')}</button>
    </div>
  );
}
// Typed: the name in a handwriting font, drawn on a canvas.
async function typedPng(name, font) {
  loadFonts();
  await document.fonts.load(`64px "${font}"`).catch(() => {});
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  ctx.font = `64px "${font}"`;
  const w = Math.ceil(ctx.measureText(name).width) + 40;
  c.width = Math.max(200, w); c.height = 110;
  ctx.font = `64px "${font}"`; ctx.fillStyle = '#0b1f38'; ctx.textBaseline = 'middle';
  ctx.fillText(name, 20, 58);
  return new Promise((ok) => c.toBlob(ok, 'image/png'));
}

// ── One signatory: name, title, contact, picture (upload / draw / type) ─
export function SignatureEditor({ sig, mine, users, canManage, onClose, onSaved }) {
  const { t, errorText } = useI18n();
  const [f, setF] = useState({ fullName: sig?.fullName || '', title: sig?.title || '', email: sig?.email || '', phone: sig?.phone || '', userId: sig?.userId || '' });
  const [mode, setMode] = useState('draw');
  const [file, setFile] = useState(null);
  const [removeWhite, setRemoveWhite] = useState(true);
  const [drawn, setDrawn] = useState(null);
  const [font, setFont] = useState(FONTS[0][0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { loadFonts(); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  async function save() {
    setBusy(true); setError('');
    try {
      const body = { fullName: f.fullName, title: f.title, email: f.email, phone: f.phone, ...(canManage && !mine ? { userId: Number(f.userId) || null } : {}) };
      const s = sig ? await api(`/workspace/doc-signatories/${sig.id}`, { method: 'PATCH', body }) : await api('/workspace/doc-signatories', { method: 'POST', body: { ...body, mine: !!mine } });
      let blob = null;
      if (mode === 'upload' && file) blob = await prepareImage(file, { maxSide: 1200, removeWhite });
      if (mode === 'draw' && drawn) blob = drawn;
      if (mode === 'type' && (f.fullName || '').trim()) blob = await typedPng(f.fullName.trim(), font);
      if (blob) {
        const fd = new FormData(); fd.append('file', new File([blob], 'signature.png', { type: blob.type || 'image/png' })); fd.append('source', mode === 'type' ? 'typed' : mode === 'draw' ? 'drawn' : 'upload');
        await api(`/workspace/doc-signatories/${s.id}/picture`, { method: 'POST', form: fd });
      }
      onSaved();
    } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  return (
    <Modal title={sig ? t('sig.edit') : mine ? t('sig.mineNew') : t('sig.new')} wide onClose={onClose}>
      <div className="ws-form">
        <label className="field"><span className="field-label">{t('sig.fullName')}</span><input value={f.fullName} onChange={(e) => set('fullName', e.target.value)} maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('sig.title')}</span><input value={f.title} onChange={(e) => set('title', e.target.value)} maxLength={120} placeholder={t('sig.titlePh')} /></label>
        <label className="field"><span className="field-label">{t('sig.email')}</span><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></label>
        <label className="field"><span className="field-label">{t('sig.phone')}</span><input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></label>
        {canManage && !mine && users?.length > 0 && (
          <label className="field ws-full"><span className="field-label">{t('sig.user')}</span>
            <select value={f.userId || ''} onChange={(e) => set('userId', e.target.value)}><option value="">{t('sig.noUser')}</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
            <span className="ws-hint">{t('sig.userHint')}</span></label>
        )}
      </div>
      <fieldset className="sig-modes">
        <legend className="field-label">{t('sig.picture')}</legend>
        <div className="ws-pills">
          {['draw', 'upload', 'type'].map((m) => <label key={m} className="ws-pill"><input type="radio" name="sig-mode" checked={mode === m} onChange={() => setMode(m)} /><span>{t(`sig.mode.${m}`)}</span></label>)}
        </div>
        {sig?.hasPicture && <p className="ws-hint">{t('sig.current')} <SignaturePicture id={sig.id} version={sig.updatedAt} className="sig-img sig-img-inline" /> — {t('sig.keepHint')}</p>}
        {mode === 'draw' && <DrawPad onChange={setDrawn} />}
        {mode === 'upload' && (
          <div className="ws-form">
            <label className="field ws-full"><span className="field-label">{t('sig.file')}</span><input type="file" accept="image/png,image/jpeg" onChange={(e) => setFile(e.target.files[0] || null)} /></label>
            <label className="field-checkbox ws-full"><input type="checkbox" checked={removeWhite} onChange={(e) => setRemoveWhite(e.target.checked)} /><span>{t('sig.removeWhite')}</span></label>
          </div>
        )}
        {mode === 'type' && (
          <div className="sig-fonts" role="radiogroup" aria-label={t('sig.font')}>
            {FONTS.map(([name]) => (
              <label key={name} className={`sig-font${font === name ? ' is-on' : ''}`}>
                <input type="radio" name="sig-font" checked={font === name} onChange={() => setFont(name)} />
                <span style={{ fontFamily: `"${name}", cursive` }}>{f.fullName || t('sig.fullName')}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>
      <Alert>{error}</Alert>
      <div className="ws-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy || !f.fullName.trim()} onClick={save}>{busy ? t('common.saving') : t('common.save')}</button>
      </div>
    </Modal>
  );
}

// ── Letterhead › "Signatures": the company's signatories ─────────────────
export function SignaturesSection() {
  const { t, errorText } = useI18n();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null); // { sig?, mine? }
  const [error, setError] = useState('');
  const load = useCallback(() => api('/workspace/doc-signatories').then(setData).catch((e) => setError(errorText(e))), [errorText]);
  useEffect(() => { load(); api('/team').then((d) => setUsers([...d.owners.map((o) => ({ id: o.id, name: o.name })), ...d.members.filter((m) => m.status === 'active').map((m) => ({ id: m.userId, name: m.name }))])).catch(() => {}); }, [load]);
  async function remove(s) {
    if (!window.confirm(t('sig.deleteConfirm', { name: s.fullName }))) return;
    try { await api(`/workspace/doc-signatories/${s.id}`, { method: 'DELETE' }); load(); } catch (e) { setError(errorText(e)); }
  }
  if (!data) return error ? <Alert>{error}</Alert> : null;
  const hasMine = data.signatories.some((s) => s.mine);
  return (
    <section className="ws-card ws-sigs">
      <div className="ws-row"><h2 className="ws-grow">{t('sig.section')}</h2>
        {!hasMine && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdit({ mine: true })}>+ {t('sig.mineNew')}</button>}
        {data.canManage && <button type="button" className="btn btn-primary btn-sm" onClick={() => setEdit({})}>+ {t('sig.new')}</button>}
      </div>
      <p className="ws-hint">{t('sig.lead')}</p>
      {data.signatories.length ? (
        <ul className="sig-list">
          {data.signatories.map((s) => (
            <li key={s.id}>
              {(s.mine || data.canManage || data.canSignOthers) && s.hasPicture ? <SignaturePicture id={s.id} version={s.updatedAt} /> : <span className="sig-img sig-img-empty">{s.hasPicture ? '✓' : '—'}</span>}
              <div className="ws-grow"><strong>{s.fullName}</strong>{s.mine && <span className="ws-badge">{t('sig.me')}</span>}<br /><span className="muted small">{[s.title, s.email].filter(Boolean).join(' · ')}</span></div>
              {s.canEdit && <span className="ws-row"><button type="button" className="btn btn-link btn-sm" onClick={() => setEdit({ sig: s, mine: s.mine })}>{t('team.edit')}</button>
                <button type="button" className="btn btn-link btn-sm is-danger" onClick={() => remove(s)}>{t('ws.lh.remove')}</button></span>}
            </li>
          ))}
        </ul>
      ) : <p className="muted small">{t('sig.none')}</p>}
      <Alert>{error}</Alert>
      {edit && <SignatureEditor sig={edit.sig} mine={edit.mine} users={users} canManage={data.canManage} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </section>
  );
}

// ── My space › Settings › "Ma signature" ─────────────────────────────────
export function MySignaturePage() {
  const { t, errorText } = useI18n();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => api('/workspace/doc-signatories').then(setData).catch((e) => setError(errorText(e))), [errorText]);
  useEffect(() => { load(); }, [load]);
  const mine = data?.signatories.find((s) => s.mine);
  return (
    <div className="acc-page">
      <div className="acc-head"><TutorialSlot place="ws.docs.signature" /><div><h1>{t('sig.myTitle')}</h1><p className="muted">{t('sig.myLead')}</p></div></div>
      <Alert>{error}</Alert>
      {/* Email signature (the same as My mailbox › Signature). */}
      <section className="ws-card" id="email-signature">
        <h2>{t('sigEmail.title')}</h2>
        <EmailSignatureEditor />
      </section>
      {data && (
        <section className="ws-card">
          {mine ? (
            <div className="sig-mine">
              {mine.hasPicture ? <SignaturePicture id={mine.id} version={mine.updatedAt} className="sig-img sig-img-large" /> : <p className="muted">{t('sig.noPicture')}</p>}
              <p><strong>{mine.fullName}</strong>{mine.title ? ` — ${mine.title}` : ''}</p>
              <button type="button" className="btn btn-primary" onClick={() => setEdit(true)}>{t('sig.change')}</button>
            </div>
          ) : (
            <div className="sig-mine"><p className="muted">{t('sig.noneMine')}</p><button type="button" className="btn btn-primary" onClick={() => setEdit(true)}>+ {t('sig.mineNew')}</button></div>
          )}
          <p className="ws-hint">{t('sig.frozenHint')}</p>
        </section>
      )}
      {edit && <SignatureEditor sig={mine} mine users={[]} canManage={false} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
    </div>
  );
}

// ── The signature blocks of a document (or a template's defaults) ────────
// value: { blocks: [{ signatoryId | me | hand, name?, title? }], layout, align, showDate, customer, initials } | null (= the author's own signature)
export function SigningPanel({ value, onChange, template = false, disabled = false }) {
  const { t } = useI18n();
  const [sigs, setSigs] = useState(null);
  useEffect(() => { api('/workspace/doc-signatories').then(setSigs).catch(() => setSigs({ signatories: [], canSignOthers: false })); }, []);
  const v = value || null;
  const count = v ? v.blocks.length : 'letterhead';
  const base = { layout: 'row', align: 'left', showDate: true, customer: false, initials: false };
  const mineId = sigs?.signatories.find((s) => s.mine)?.id;
  const emptyBlock = () => (template ? { me: true } : mineId ? { signatoryId: mineId } : { hand: true });
  const setCount = (n) => {
    if (n === 'letterhead') { onChange(null); return; }
    const blocks = [...(v?.blocks || [])].slice(0, n);
    while (blocks.length < n) blocks.push(blocks.length ? { hand: true } : emptyBlock());
    onChange({ ...base, ...(v || {}), blocks });
  };
  const setBlock = (i, choice) => {
    const blocks = v.blocks.map((b, k) => (k !== i ? b : choice === 'me' ? { me: true } : choice === 'hand' ? { hand: true } : { signatoryId: Number(choice) }));
    onChange({ ...v, blocks });
  };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const choiceOf = (b) => (b.me ? 'me' : b.hand || !b.signatoryId ? 'hand' : String(b.signatoryId));
  const radio = (name, val, cur, label, on) => <label key={String(val)} className="ws-pill"><input type="radio" name={name} checked={cur === val} disabled={disabled} onChange={() => on(val)} /><span>{label}</span></label>;
  return (
    <fieldset className="ws-layout-group sig-panel">
      <legend>{t('sig.panel')}</legend>
      <span className="field-label">{t('sig.count')}</span>
      <div className="ws-pills">
        {!template && radio('sig-count', 'letterhead', count, t('sig.countLetterhead'), setCount)}
        {[0, 1, 2, 3].map((n) => radio('sig-count', n, count, n ? String(n) : t('sig.countNone'), setCount))}
      </div>
      {v && v.blocks.map((b, i) => (
        <label key={i} className="field"><span className="field-label">{t('sig.block', { n: i + 1 })}</span>
          <select value={choiceOf(b)} disabled={disabled} onChange={(e) => setBlock(i, e.target.value)}>
            {template && <option value="me">{t('sig.optMe')}</option>}
            {(sigs?.signatories || []).map((s) => <option key={s.id} value={s.id} disabled={!s.canUse && !template}>{s.fullName}{s.title ? ` — ${s.title}` : ''}{s.mine ? ` (${t('sig.me')})` : ''}{!s.canUse && !template ? ` — ${t('sig.notAllowed')}` : ''}</option>)}
            <option value="hand">{t('sig.optHand')}</option>
          </select>
        </label>
      ))}
      {v && v.blocks.length > 0 && (
        <>
          {v.blocks.length > 1 && <><span className="field-label">{t('sig.layout')}</span><div className="ws-pills">{['row', 'column'].map((k) => radio('sig-layout', k, v.layout, t(`sig.layoutK.${k}`), (x) => set('layout', x)))}</div></>}
          <span className="field-label">{t('ws.lay.position')}</span>
          <div className="ws-pills">{['left', 'center', 'right'].map((k) => radio('sig-align', k, v.align, t(`ws.lay.pos.${k}`), (x) => set('align', x)))}</div>
          <label className="field-checkbox"><input type="checkbox" checked={!!v.showDate} disabled={disabled} onChange={(e) => set('showDate', e.target.checked)} /><span>{t('sig.showDate')}</span></label>
          <label className="field-checkbox"><input type="checkbox" checked={!!v.initials} disabled={disabled} onChange={(e) => set('initials', e.target.checked)} /><span>{t('sig.initials')}</span></label>
        </>
      )}
      {v && <label className="field-checkbox"><input type="checkbox" checked={!!v.customer} disabled={disabled} onChange={(e) => set('customer', e.target.checked)} /><span>{t('sig.customer')}</span></label>}
      {!template && sigs && !sigs.signatories.some((s) => s.mine) && <p className="ws-hint">{t('sig.noOwnHint')}</p>}
    </fieldset>
  );
}
